// Utility functions for recurring items

/**
 * Formats recurring days array into a short string (e.g., "M, W, F" or "S")
 * @param days Array of weekday numbers (0=Sunday, 6=Saturday)
 * @returns Formatted string of day abbreviations
 */
export function formatRecurringDays(days: number[]): string {
  if (!days || days.length === 0) return '';
  
  const dayLabels = ['U', 'M', 'T', 'W', 'R', 'F', 'S'];
  const sortedDays = [...days].sort((a, b) => a - b);
  
  // Handle special cases for readability
  if (sortedDays.length === 7) {
    return 'Daily';
  }
  
  if (sortedDays.length === 5 && !sortedDays.includes(0) && !sortedDays.includes(6)) {
    return 'Weekdays';
  }
  
  if (sortedDays.length === 2 && sortedDays.includes(0) && sortedDays.includes(6)) {
    return 'Weekends';
  }
  
  // Map days to their labels
  const labels = sortedDays.map(day => dayLabels[day]);
  
  return labels.join(', ');
}

/**
 * Converts a number to its ordinal form (1st, 2nd, 3rd, etc.)
 */
function getOrdinal(n: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
}

/**
 * Formats month days array into a short string with ordinals (e.g., "1st, 15th, 31st")
 * @param days Array of day numbers (1-31)
 * @returns Formatted string of ordinal days
 */
export function formatMonthDays(days: number[]): string {
  if (!days || days.length === 0) return '';
  
  const sortedDays = [...days].sort((a, b) => a - b);
  const ordinalDays = sortedDays.map(d => getOrdinal(d));
  
  if (ordinalDays.length === 1) {
    return ordinalDays[0];
  }
  
  if (ordinalDays.length === 2) {
    return ordinalDays.join(' & ');
  }
  
  // For 3 or more, use commas with "and" before last
  return ordinalDays.slice(0, -1).join(', ') + ' & ' + ordinalDays[ordinalDays.length - 1];
}

/**
 * Gets the current day of the week (0=Sunday, 6=Saturday)
 */
export function getCurrentDayOfWeek(): number {
  return new Date().getDay();
}

/**
 * Gets the user's timezone
 */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
